/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { DOMAttributes, ReactNode } from 'react';
import { Tooltip } from 'antd';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import doReturnTheme from '../../../styles/themeEngine';
import { EStyleObj, EStyle } from '../../../types';

export interface SVFIconButtonProps {
  icon: ReactNode; // can be SVG url or inline SVG
  containerStyle?: EStyle;
  tooltip?: any;
  backgroundOff?: boolean;
  click?: DOMAttributes<HTMLDivElement>['onClick'];
  color?: string; // allow color override
  disabled?: boolean;
  badge?: string;
  badgeStyle?: EStyle;
  textStyle?: EStyle;
  widthTooltip?: string;
  width?: string | number; // new prop
  height?: string | number; // new prop
}

const SVFIconButton: React.FC<SVFIconButtonProps> = ({
  icon,
  containerStyle,
  tooltip,
  backgroundOff,
  click,
  color,
  disabled,
  badge,
  badgeStyle,
  widthTooltip = '',
  width = 24,
  height = 24,
}) => {
  const { currentTheme } = useThemeSwitcher();
  const theme = doReturnTheme(currentTheme);

  const style: EStyleObj = {
    container: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '20px',
      minHeight: '30px',
      minWidth: '30px',
      outline: 'none',
    },
    containerWithHover: {
      cursor: 'pointer',
      ':hover': {
        backgroundColor: theme.colors.primaryLight,
      },
    },
    containerDisabled: {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
    badgeContainer: {
      position: 'absolute',
      borderRadius: '50%',
      fontSize: '9px',
      height: '11px',
      width: '11px',
      backgroundColor: theme.colors.primaryLight,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontWeight: 600,
    },
    iconStyle: {
      width,
      height,
      color: color || theme.colors.darkGray,
    },
  };

  return (
    <div
      css={[
        style.container,
        !backgroundOff && !disabled && style.containerWithHover,
        disabled && style.containerDisabled,
        containerStyle,
      ]}
      onClick={!disabled ? click : undefined}
      role="button"
      tabIndex={0}>
      <Tooltip
        overlayStyle={{ maxWidth: widthTooltip, width: widthTooltip }}
        placement="bottom"
        title={tooltip}>
        <div css={{ position: 'relative' }}>
          {icon}
          {badge && <div css={[style.badgeContainer, badgeStyle]}>{badge}</div>}
        </div>
      </Tooltip>
    </div>
  );
};

export default SVFIconButton;
